import {
  and,
  collection,
  getDocs,
  limit,
  or,
  query,
  where,
} from 'firebase/firestore';
import Failure from '../../../core/Failure';
import { FirestoreCollections, firestore } from '../../../firebase.config';
import UserDoc from '../../models/UserDoc';
import { UserRole } from '@leedpulse-monorepo/enums';

async function searchUsers(
  searchQuery: string,
  roleConstraint?: UserRole
): Promise<UserDoc[]> {
  try {
    // Sanitize search query
    const sanitizedSearchQuery = searchQuery
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9\s@.]/g, '')
      .toLowerCase()
      .trim();
      console.log("sanitizedSearchQuery", sanitizedSearchQuery);

    if (!sanitizedSearchQuery) {
      return [];
    }

    const usersCollection = collection(firestore, FirestoreCollections.users);
    const sanitizedPhone = sanitizedSearchQuery.replace(/\D/g, '');
    const searchFields = ['email', 'email_authentication', 'firstname', 'lastname', 'phone'];

    // Helper to build query with optional role constraint
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const buildQuery = (...constraints: any[]) => {
      const allConstraints = roleConstraint
        ? [...constraints, where('role', '==', roleConstraint)]
        : constraints;
      return query(usersCollection, ...allConstraints);
    };

    // 1. Exact search
    const exactSearchQuery = roleConstraint
      ? query(
          usersCollection,
          and(
            or(...searchFields.map(field => where(field, '==', sanitizedSearchQuery))),
            where('role', '==', roleConstraint)
          )
        )
      : query(
          usersCollection,
          and(or(...searchFields.map(field => where(field, '==', sanitizedSearchQuery))))
        );
    const exactSearchResults = await getDocs(exactSearchQuery);

    // 2. StartsWith search (Firestore technique: >= query and < query with next character)
    const startsWithEnd = sanitizedSearchQuery.slice(0, -1) + 
      String.fromCharCode(sanitizedSearchQuery.charCodeAt(sanitizedSearchQuery.length - 1) + 1);
    
    const startsWithQueries = searchFields.map(field =>
      buildQuery(
        where(field, '>=', sanitizedSearchQuery),
        where(field, '<', startsWithEnd),
        limit(200)
      )
    );
    const startsWithResults = await Promise.all(startsWithQueries.map(q => getDocs(q)));
    const startsWithDocs = startsWithResults.flatMap(snap => snap.docs);

    // 3. Deduplicate and score results
    const allDocs = [...exactSearchResults.docs, ...startsWithDocs];
    const uniqueDocs = new Map();
    allDocs.forEach((doc) => {
      if (!uniqueDocs.has(doc.id)) {
        uniqueDocs.set(doc.id, doc);
      }
    });

    // 4. Calculate scores with bonuses
    const resultsWithScore = Array.from(uniqueDocs.values()).map((doc) => {
      const data = doc.data();

      // Normalize fields for comparison
      const normalize = (str: string) => (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const normalizePhone = (str: string) => (str || '').replace(/\D/g, '');
      
      const normalized = {
        firstname: normalize(data.firstname),
        lastname: normalize(data.lastname),
        email: normalize(data.email),
        emailAuth: normalize(data.email_authentication),
        phone: normalizePhone(data.phone),
      };

      // Calculate bonus based on match type
      let bonus = 0;
      const checkMatch = (value: string, query: string) => {
        if (value === query) return 1.0;
        if (value.startsWith(query)) return 0.9;
        if (value.includes(query)) return 0.8;
        return 0;
      };

      bonus = Math.max(
        checkMatch(normalized.firstname, sanitizedSearchQuery),
        checkMatch(normalized.lastname, sanitizedSearchQuery),
        checkMatch(normalized.email, sanitizedSearchQuery),
        checkMatch(normalized.emailAuth, sanitizedSearchQuery),
        checkMatch(normalized.phone, sanitizedPhone)
      );

      return {
        user: UserDoc.fromFirestore(doc),
        score: bonus,
      };
    });

    // 6. Filter, sort and limit results
    return resultsWithScore
      .filter((result) => result.score > 0.3)
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return (a.user.firstname || '').toLowerCase().localeCompare((b.user.firstname || '').toLowerCase());
      })
      .slice(0, 50)
      .map((result) => result.user);
  } catch (e) {
    console.log('error: ', e);
    throw new SearchUsersFailure();
  }
}

export class SearchUsersFailure extends Failure {
  constructor(message = 'Unknown error', code = 'unknown-error') {
    super(message, code);
  }
}

export default searchUsers;
